import React from 'react';
import { X, Check, Star, Phone, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { servicesEn } from '../mock';

const ServiceDetailModal = ({ service, isOpen, onClose }) => {
  const [isConsulting, setIsConsulting] = React.useState(false);
  const { t, currentLanguage } = useLanguage();
  
  // Get service data based on current language
  const getCurrentService = () => {
    if (!service) return null;
    
    if (currentLanguage === 'en') {
      const englishService = servicesEn.find(s => s.id === service.id);
      return englishService || service;
    }
    return service;
  };
  
  const currentService = getCurrentService();

  // Early return if modal should not be shown
  if (!isOpen || !service) {
    return null;
  }

  const handleConsultation = () => {
    const whatsappMessage = t('whatsapp.serviceInquiry').replace('{serviceName}', currentService?.title || 'Eventime');
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappURL = `https://wa.me/+6285179649711?text=${encodedMessage}`;
    window.open(whatsappURL, '_blank');
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="modal-overlay"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease-out'
      }}
    >
      <div 
        className="modal-content"
        style={{
          background: 'linear-gradient(135deg, var(--bg-card) 0%, #ffffff 100%)',
          borderRadius: '32px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 69, 52, 0.3)',
          border: '1px solid rgba(211, 255, 98, 0.3)',
          animation: 'slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(0, 0, 0, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            zIndex: 10
          }}
          onMouseOver={(e) => {
            e.target.style.background = 'rgba(0, 0, 0, 0.2)';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'rgba(0, 0, 0, 0.1)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          <X size={20} color="var(--text-primary)" />
        </button>

        {/* Header Image */}
        <div style={{
          height: '250px',
          borderRadius: '32px 32px 0 0',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <img 
            src={currentService.image}
            alt={currentService.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            padding: 'var(--spacing-large)',
            color: 'white'
          }}>
            <h2 className="heading-2" style={{ color: 'white', marginBottom: '8px' }}>
              {currentService.title}
            </h2>
            <p style={{ opacity: '0.9', fontSize: '1.1rem' }}>
              {currentService.description}
            </p>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--spacing-xl)' }}>
          {/* Detailed Description */}
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3 className="heading-3" style={{ 
              marginBottom: 'var(--spacing-medium)',
              color: 'var(--brand-dark)'
            }}>
              {t('services.modal.aboutService')}
            </h3>
            <p className="body-large" style={{ lineHeight: '1.7' }}>
              {currentService.detailedDescription}
            </p>
          </div>

          {/* Features */}
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3 className="heading-3" style={{ 
              marginBottom: 'var(--spacing-medium)',
              color: 'var(--brand-dark)'
            }}>
              {t('services.modal.features')}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'var(--spacing-medium)'
            }}>
              {currentService.features.map((feature, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--spacing-small)',
                    padding: 'var(--spacing-medium)',
                    background: 'rgba(211, 255, 98, 0.1)',
                    borderRadius: '16px',
                    border: '1px solid rgba(211, 255, 98, 0.3)'
                  }}
                >
                  <div style={{
                    background: 'var(--brand-primary)',
                    borderRadius: '50%',
                    padding: '4px',
                    marginTop: '2px'
                  }}>
                    <Check size={12} color="var(--brand-dark)" />
                  </div>
                  <span className="body-medium" style={{ flex: 1 }}>
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Past Events */}
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3 className="heading-3" style={{ 
              marginBottom: 'var(--spacing-medium)',
              color: 'var(--brand-dark)'
            }}>
              {t('services.modal.pastEvents')}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--spacing-medium)'
            }}>
              {currentService.pastEvents.map((event, index) => (
                <div 
                  key={index}
                  style={{
                    padding: 'var(--spacing-medium)',
                    background: 'var(--bg-card)',
                    borderRadius: '16px',
                    border: '1px solid var(--border-light)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-small)'
                  }}
                >
                  <Star size={16} color="var(--brand-primary)" />
                  <span className="body-medium">{event}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Packages */}
          <div style={{ marginBottom: 'var(--spacing-xl)' }}>
            <h3 className="heading-3" style={{ 
              marginBottom: 'var(--spacing-medium)',
              color: 'var(--brand-dark)'
            }}>
              {t('services.modal.packages')}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--spacing-large)'
            }}>
              {currentService.packages.map((pkg, index) => (
                <div 
                  key={index}
                  className="network-card hover-lift"
                  style={{
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {index === 1 && (
                    <div style={{
                      position: 'absolute',
                      top: '0',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'var(--brand-primary)',
                      color: 'var(--brand-dark)',
                      padding: '4px 16px',
                      borderRadius: '0 0 12px 12px',
                      fontSize: '0.8rem',
                      fontWeight: '600'
                    }}>
                      {t('services.modal.popular')}
                    </div>
                  )}
                  
                  <h4 className="heading-3" style={{ 
                    marginBottom: 'var(--spacing-small)',
                    marginTop: index === 1 ? 'var(--spacing-medium)' : '0'
                  }}>
                    {pkg.name}
                  </h4>
                  
                  <p className="body-large" style={{
                    color: 'var(--text-primary)',
                    marginTop: 'var(--spacing-medium)'
                  }}>
                    {pkg.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div style={{
            background: 'linear-gradient(135deg, var(--brand-primary) 0%, rgba(211, 255, 98, 0.8) 100%)',
            padding: 'var(--spacing-xl)',
            borderRadius: '24px',
            textAlign: 'center',
            border: `2px solid var(--brand-dark)`
          }}>
            <h3 className="heading-3" style={{ 
              color: 'var(--brand-dark)',
              marginBottom: 'var(--spacing-medium)'
            }}>
              {t('services.modal.interested')}
            </h3>
            <p className="body-large" style={{ 
              color: 'var(--brand-dark)',
              marginBottom: 'var(--spacing-large)',
              opacity: '0.8'
            }}>
              {t('services.modal.ctaDescription')}
            </p>
            
            <div style={{
              display: 'flex',
              gap: 'var(--spacing-medium)',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                className="btn-primary"
                onClick={handleConsultation}
                disabled={isConsulting}
                style={{
                  background: 'var(--brand-dark)',
                  opacity: isConsulting ? 0.7 : 1,
                  cursor: isConsulting ? 'not-allowed' : 'pointer'
                }}
              >
                {isConsulting ? (
                  t('services.modal.processing')
                ) : (
                  <>
                    <Phone size={20} className="icon-enhanced" />
                    <span>{t('services.modal.freeConsultation')}</span>
                  </>
                )}
              </button>

              <button
                className="btn-secondary"
                onClick={() => {
                  const whatsappMessage = t('whatsapp.contactInquiry').replace('{serviceName}', currentService?.title || 'Eventime');
                  const encodedMessage = encodeURIComponent(whatsappMessage);
                  const whatsappURL = `https://wa.me/+6285179649711?text=${encodedMessage}`;
                  window.open(whatsappURL, '_blank');
                  onClose();
                }}
                style={{
                  background: 'transparent',
                  borderColor: 'var(--brand-dark)',
                  color: 'var(--brand-dark)'
                }}
              >
                <span>{t('events.contactUs')}</span>
                <ArrowRight size={20} className="icon-enhanced" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @media (max-width: 768px) {
          .modal-content {
            margin: 10px;
            border-radius: 24px !important;
          }
          
          .modal-content > div:last-child {
            padding: var(--spacing-medium) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ServiceDetailModal;